import React, { FC, useRef, useState } from 'react';
import styles from './Contact.module.scss';
import emailjs from '@emailjs/browser';
import { useForm, SubmitHandler } from 'react-hook-form';

type FormValues = {
  user_name: string;
  user_email: string;
  message: string;
};

type ContactMessage = {
  type: 'success' | 'error';
  message: string;
};

type EmailJSError = {
  status?: number;
  text?: string;
  message?: string;
};

const Contact: FC = () => {
  const genericErrorMessage = 'Something went wrong while sending your message. Please try again later.';

  const emailJsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID ?? 'service_uom1o4w';
  const emailJsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID ?? 'template_95sy3yl';
  const emailJsPublicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY ?? '5oug5V7I2439DdiEd';

  const form = useRef() as React.MutableRefObject<HTMLFormElement>;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      user_email: '',
      user_name: '',
      message: '',
    },
  });

  const [contactMessage, setContactMessage] = useState<ContactMessage>();
  const [isWaiting, setIsWaiting] = useState<boolean>(false);

  const onSubmit: SubmitHandler<FormValues> = (data: FormValues, event?: React.BaseSyntheticEvent) => {
    if (event) {
      event.preventDefault();
    }

    setIsWaiting(true);
    sendEmail();
  };

  const sendEmail = async () => {
    if (!emailJsServiceId || !emailJsTemplateId || !emailJsPublicKey) {
      setIsWaiting(false);
      setContactMessage({
        type: 'error',
        message: genericErrorMessage,
      });

      console.error('EmailJS configuration is missing.');

      return;
    }

    try {
      await emailjs.sendForm(emailJsServiceId, emailJsTemplateId, form.current, emailJsPublicKey);
      setIsWaiting(false);
      setContactMessage({
        type: 'success',
        message: 'The message was successfully sent!',
      });
      reset();
    } catch (error) {
      const emailError = error as EmailJSError;

      setIsWaiting(false);
      setContactMessage({
        type: 'error',
        message: genericErrorMessage,
      });
      console.error('EmailJS error:', emailError);
    }
  };

  return (
    <section id="contact">
      <div className={styles.container}>
        <div className={styles.row}>
          <h2 className={styles['header']}>Contact</h2>
          <p className={styles.intro}>
            Looking for support with frontend architecture, product implementation, or team collaboration? Let us talk.
          </p>
          <div className={styles['contact-form']}>
            {contactMessage && (
              <p className={contactMessage.type === 'error' ? styles['error-message'] : styles['success-message']}>
                {contactMessage.message}
              </p>
            )}
            <form ref={form} onSubmit={handleSubmit(onSubmit)} className={styles.form} autoComplete="off" noValidate>
              <div className={styles['form-field']}>
                <label className={styles.label} htmlFor="user_name">
                  Name
                </label>
                <input
                  id="user_name"
                  placeholder="Your name"
                  {...register('user_name', {
                    required: 'This field is required',
                    minLength: {
                      value: 3,
                      message: 'The field must be 3 or more characters',
                    },
                    maxLength: {
                      value: 25,
                      message: 'The field has a max number of 25 characters',
                    },
                  })}
                  className={`${styles['input-text']} ${
                    errors.user_name ? styles['error-input'] : styles['without-error-input']
                  }`}
                />
                <p className={styles['error-form-message']}>{errors.user_name?.message}</p>
              </div>
              <div className={styles['form-field']}>
                <label className={styles.label} htmlFor="user_email">
                  E-mail
                </label>
                <input
                  id="user_email"
                  placeholder="your@email.com"
                  {...register('user_email', {
                    required: 'This field is required',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  className={`${styles['input-text']} ${
                    errors.user_email ? styles['error-input'] : styles['without-error-input']
                  }`}
                />
                <p className={styles['error-form-message']}>{errors.user_email?.message}</p>
              </div>
              <div className={styles['form-field']}>
                <label className={styles.label} htmlFor="message">
                  Message
                </label>
                <textarea
                  id="message"
                  rows={5}
                  placeholder="Tell me about your project"
                  {...register('message', {
                    required: 'This field is required',
                  })}
                  className={`${styles['input-text']} ${
                    errors.message ? styles['error-input'] : styles['without-error-input']
                  }`}
                />
                <p className={styles['error-form-message']}>{errors.message?.message}</p>
              </div>
              <div className={styles['form-field']}>
                <button className={styles['submit-btn']} type="submit" value="Submit">
                  {isWaiting ? (
                    <div className={`${styles.spinner} ${styles.spinner3}`}></div>
                  ) : (
                    <span>Send Message</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
